import moment from 'moment';
import { useCallback, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { TChannelParticipant } from '../../../api/messages';
import { messageStrings } from '../../../constants/strings';
import { useCurrentUser } from 'hooks';
import { Icon } from '../../Icon';
import { Loading } from '../../Loading';
import { Text } from '../../Text';
import { MessageItem } from '../MessageItem';
import { MediaLightbox, type LightboxMedia } from '../../MediaLightbox';

type MessageDetail = {
  channel: string;
  message: {
    text?: string;
    file?: unknown;
    media?: Array<{
      type?: string;
      url?: string;
      fileName?: string;
      originalHeight?: number;
      originalWidth?: number;
      poster?: string;
    }>;
  };
  timetoken: string | number;
  uuid?: string;
  url?: string;
};

type Props = {
  isLoading?: boolean;
  messages: MessageDetail[];
  fetchMore: () => void;
  hasMore: boolean;
  members: { [x: string]: TChannelParticipant };
  enableAi?: boolean;
  selectedTimetokens?: Set<string>;
  onToggleSelect?: (timetoken: string, text: string) => void;
  onShowAiHistory?: (timetoken: string) => void;
  inquiryChatMode?: boolean;
  canDelete?: boolean;
  onDeleteRequest?: (timetoken: string) => void;
};

const list = Array.from(Array(10).keys()).map(() => Math.random() > 0.5);
const LoadingList = () => {
  return (
    <>
      {list.map((value, index) => {
        const animation = value
          ? `animate-pulse`
          : `animate-pulse [animation-delay:-0.1s]`;
        return (
          <div
            key={index}
            className={`flex w-full ${
              value ? 'items-start' : 'items-end justify-end'
            }`}
          >
            {value && (
              <div
                className={`h-11 w-11 rounded-full bg-gray-300 mr-3 ${animation}`}
              ></div>
            )}
            <div
              className={`rounded-xl border border-neutral-200 w-[35%] bg-gray-300 py-2 text-gray-300 ${animation}`}
            >
              ...Loading
            </div>
          </div>
        );
      })}
    </>
  );
};

export function MessageList({
  isLoading,
  messages,
  fetchMore,
  hasMore,
  members,
  enableAi = false,
  selectedTimetokens,
  onToggleSelect,
  onShowAiHistory,
  inquiryChatMode = false,
  canDelete = false,
  onDeleteRequest,
}: Props) {
  const { data: currentUser } = useCurrentUser();
  const [lightboxMedia, setLightboxMedia] = useState<LightboxMedia | null>(
    null,
  );

  const handleMessageTime = useCallback(
    (msgTimeToken: string, beforeMsgToken?: string) => {
      const msgTime = +msgTimeToken / 10000;
      const beforeMsgTime = +(beforeMsgToken || 0) / 10000;
      let displayTime;
      const isOneHourDiff = moment(msgTime).diff(beforeMsgTime, 'hours') > 1;
      if (
        !moment(msgTime).isSame(beforeMsgTime, 'day') ||
        isNaN(beforeMsgTime)
      ) {
        displayTime = moment(msgTime).format('ll (dd) HH:mm');
      } else if (isOneHourDiff) {
        displayTime = moment(msgTime).format('HH:mm');
      }
      return { displayTime, isOneHourDiff };
    },
    [],
  );

  const handleUserInfo = useCallback(
    (isOneHourDiff: boolean, publisher: string, beforePublisher?: string) => {
      const isMyMessage = currentUser?.uuid === publisher;
      const member = members[publisher];
      const isFromOrgMember = member?.provider === 'MANAGEMENT_APPLICATION';
      const isMessageFromManagement = inquiryChatMode
        ? isMyMessage
        : isMyMessage || isFromOrgMember;

      // Debug logging to check UUID matching (development only)
      if (process.env.NODE_ENV === 'development' && !isMyMessage) {
        const showUserInfo = publisher !== beforePublisher || isOneHourDiff;
        if (showUserInfo && !member) {
          console.warn('⚠️ [UUID MISMATCH] Member not found for publisher:', {
            publisher,
            availableUUIDs: Object.keys(members),
            members: Object.values(members).map((m) => ({
              uuid: m.uuid,
              name: m.name,
            })),
            message:
              'UUID in message does not match any UUID in participants. Ensure BE returns correct UUID.',
          });
        }
      }

      // zip をアップロードできるのは管理画面だけだが、PubNub の publish key は male バンドルにも
      // あるため、組織メンバーが publish した zip だけ開けるようにする。
      // お問い合わせチャットは isMessageFromManagement = isMyMessage なので使えないが、
      // 参加者は必ず組織メンバー（provider MANAGEMENT_APPLICATION）なので isFromOrgMember で相手（運営）も開ける
      const canOpenZip = isMyMessage || isFromOrgMember;

      return {
        isMessageFromManagement,
        isMyMessage,
        canOpenZip,
      };
    },
    [members, currentUser?.uuid, inquiryChatMode],
  );

  return (
    <div
      id="scrollableMessage"
      className="h-full gap-4 overflow-y-auto p-4 flex flex-col-reverse"
    >
      {isLoading ? (
        <LoadingList />
      ) : !messages.length ? (
        <div className="m-auto flex flex-col items-center gap-3 px-6 text-center text-muted">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-neutral-25">
            <Icon icon="menuChat" className="h-7 w-7 text-neutral-400" />
          </span>
          <Text className="tracking-wide text-muted" center>
            {messageStrings.startMessage}
          </Text>
        </div>
      ) : (
        <>
          <InfiniteScroll
            next={fetchMore}
            inverse={true}
            hasMore={hasMore}
            loader={
              <div className="py-[36px] flex justify-center items-center">
                <Loading />
              </div>
            }
            dataLength={messages.length}
            scrollThreshold={0.5}
            className="gap-4 flex flex-col-reverse overflow-hidden"
            scrollableTarget="scrollableMessage"
          >
            {messages.map((item, index, arr) => {
              const { displayTime, isOneHourDiff } = handleMessageTime(
                String(item.timetoken),
                arr[index + 1] ? String(arr[index + 1].timetoken) : undefined,
              );

              const { isMessageFromManagement, isMyMessage, canOpenZip } =
                handleUserInfo(
                  isOneHourDiff,
                  item.uuid || '',
                  arr[index + 1]?.uuid,
                );

              return (
                <div key={item.timetoken} className="">
                  {displayTime && (
                    <Text center className="mb-4">
                      {displayTime}
                    </Text>
                  )}
                  <MessageItem
                    message={item}
                    isMessageFromManagement={isMessageFromManagement}
                    enableAi={enableAi}
                    selected={selectedTimetokens?.has(String(item.timetoken))}
                    onToggleSelect={onToggleSelect}
                    onShowAiHistory={onShowAiHistory}
                    canDelete={canDelete && isMyMessage}
                    onDeleteRequest={onDeleteRequest}
                    onMediaClick={setLightboxMedia}
                    canOpenZip={canOpenZip}
                  />
                </div>
              );
            })}
          </InfiniteScroll>
        </>
      )}
      <MediaLightbox
        media={lightboxMedia}
        onClose={() => setLightboxMedia(null)}
      />
    </div>
  );
}
